package org.lennycodes.gradematewebsite.components

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.icons.fa.IconCategory
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.lennycodes.gradematewebsite.components.style.FooterStyle
import org.lennycodes.gradematewebsite.components.style.GradeMateColors
import org.lennycodes.gradematewebsite.components.style.toRgba

private const val Ease = "cubic-bezier(0.22, 1, 0.36, 1)"
private const val EaseOutBack = "cubic-bezier(0.34, 1.56, 0.64, 1)"

private fun prefersReducedMotion() = window.matchMedia("(prefers-reduced-motion: reduce)").matches

/** A footer link with an animated underline and arrow reveal. */
@Composable
private fun FooterNavLink(
    href: String,
    label: String
) {
    var hovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int) = if (reduce) 0 else ms

    A(
        href = href,
        attrs = Modifier
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("position", "relative")
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "6px")
                property("text-decoration", "none")
                property("font-size", "13px")
                property("font-weight", "500")
                property("letter-spacing", "0.01em")
                property(
                    "color",
                    if (hovered) "#ffffff" else GradeMateColors.TextOnDarkSecondary.toRgba()
                )
                property(
                    "transition",
                    "color ${t(220)}ms ease"
                )
            }
            .toAttrs()
    ) {
        Text(label)

        // Underline that grows from the left on hover
        Span(
            Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("left", "0")
                    property("bottom", "-4px")
                    property("height", "1px")
                    property("width", "100%")
                    property("background-color", "#ffffff")
                    property("transform-origin", "left")
                    property(
                        "transform",
                        if (hovered) "scaleX(1)" else "scaleX(0)"
                    )
                    property(
                        "transition",
                        "transform ${t(300)}ms $Ease"
                    )
                }.toAttrs()
        ) {}
    }
}

/** A social icon chip in the footer. */
@Composable
private fun FooterSocial(
    href: String,
    iconName: String,
    iconCategory: IconCategory,
    label: String
) {
    var hovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int) = if (reduce) 0 else ms

    A(
        href = href,
        attrs = Modifier
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("width", "38px")
                property("height", "38px")
                property("border-radius", "10px")
                property("text-decoration", "none")
                property(
                    "background-color",
                    if (hovered) GradeMateColors.Primary.toRgba()
                    else GradeMateColors.Primary.toRgba(alpha = 0.12f)
                )
                property(
                    "color",
                    if (hovered) "#ffffff" else GradeMateColors.Primary.toRgba()
                )
                property(
                    "box-shadow",
                    if (hovered) "0 12px 22px -10px ${GradeMateColors.Primary.toRgba(alpha = 0.6f)}"
                    else "none"
                )
                property(
                    "transform",
                    if (hovered) "translateY(-3px) scale(1.05)" else "translateY(0) scale(1)"
                )
                property(
                    "transition",
                    "background-color ${t(220)}ms ease, " +
                            "color ${t(220)}ms ease, " +
                            "box-shadow ${t(250)}ms ease, " +
                            "transform ${t(350)}ms $EaseOutBack"
                )
            }
            .toAttrs {
                attr("aria-label", label)
                attr("title", label)
            }
    ) {
        FaIcon(
            name = iconName,
            style = iconCategory,
            modifier = Modifier.fontSize(15.px)
        )
    }
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
@Composable
fun Footer() {
    val year = remember { js("new Date().getFullYear()") as Int }
    val reduce = remember { prefersReducedMotion() }

    Div(
        FooterStyle
            .toModifier()
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")

                // A soft brand-coloured glow in the top-right corner
                property(
                    "background-image",
                    "radial-gradient(50% 60% at 95% 0%, ${GradeMateColors.Primary.toRgba(alpha = 0.12f)}, transparent 70%)"
                )
            }.toAttrs()
    ) {
        // Top hairline — a gradient that fades at both ends
        Div(
            Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "absolute")
                    property("top", "0")
                    property("left", "0")
                    property("right", "0")
                    property("height", "1px")
                    property(
                        "background",
                        "linear-gradient(90deg, transparent, ${GradeMateColors.Primary.toRgba(alpha = 0.4f)}, transparent)"
                    )
                    property("pointer-events", "none")
                }.toAttrs()
        ) {}

        Column(
            Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "1")
                    property("gap", "clamp(28px, 4vw, 40px)")
                }
        ) {

            // ---------------------------------------------------------
            // TOP ROW — brand on the left, links + social on the right
            // ---------------------------------------------------------
            Div(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("gap", "32px")
                    }.toAttrs()
            ) {

                // -------- Brand block --------
                Div(
                    Modifier
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "14px")
                        }.toAttrs()
                ) {
                    // Logo tile
                    Div(
                        Modifier
                            .size(42.px)
                            .borderRadius(12.px)
                            .styleModifier {
                                property("overflow", "hidden")
                                property("flex-shrink", "0")
                                property(
                                    "box-shadow",
                                    "0 8px 20px -10px ${GradeMateColors.Primary.toRgba(alpha = 0.5f)}"
                                )
                            }.toAttrs()
                    ) {
                        Img(
                            src = "/grademate.png",
                            attrs = Modifier
                                .styleModifier {
                                    property("width", "100%")
                                    property("height", "100%")
                                    property("display", "block")
                                    property("object-fit", "cover")
                                }
                                .toAttrs()
                        )
                    }

                    Column(Modifier.gap(2.px)) {
                        P(
                            Modifier
                                .margin(0.px)
                                .fontSize(16.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Colors.White)
                                .styleModifier {
                                    property("letter-spacing", "-0.02em")
                                    property("line-height", "1.1")
                                }.toAttrs()
                        ) {
                            Text("GradeMate")
                        }
                        P(
                            Modifier
                                .margin(0.px)
                                .fontSize(11.px)
                                .fontWeight(FontWeight.Medium)
                                .styleModifier {
                                    property("color", GradeMateColors.TextOnDarkSecondary.toRgba(alpha = 0.7f))
                                    property("letter-spacing", "0.14em")
                                    property("text-transform", "uppercase")
                                    property("line-height", "1")
                                }
                                .toAttrs()
                        ) {
                            Text("Smarter Grading")
                        }
                    }
                }

                // -------- Right side: nav links + social --------
                Div(
                    Modifier
                        .styleModifier {
                            property("display", "flex")
                            property("flex-wrap", "wrap")
                            property("align-items", "center")
                            property("gap", "clamp(24px, 3vw, 40px)")
                        }.toAttrs()
                ) {

                    // Nav links
                    Div(
                        Modifier
                            .styleModifier {
                                property("display", "flex")
                                property("flex-wrap", "wrap")
                                property("align-items", "center")
                                property("gap", "clamp(16px, 2.5vw, 28px)")
                            }.toAttrs()
                    ) {
                        FooterNavLink("#home", "Home")
                        FooterNavLink("#docs", "Documentation")
                        FooterNavLink("#downloads", "Downloads")
                        FooterNavLink("#contact", "Contact")
                    }

                    // Divider
                    Div(
                        Modifier
                            .styleModifier {
                                property("width", "1px")
                                property("height", "24px")
                                property("background-color", "rgba(255, 255, 255, 0.12)")
                                property("flex-shrink", "0")
                            }.toAttrs()
                    ) {}

                    // Social icons
                    Div(
                        Modifier
                            .styleModifier {
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "8px")
                            }.toAttrs()
                    ) {
                        FooterSocial(
                            href = "mailto:lennycodes2000@gmail.com",
                            iconName = "envelope",
                            iconCategory = IconCategory.SOLID,
                            label = "Email"
                        )
                        FooterSocial(
                            href = "https://wa.me/255686777093",
                            iconName = "whatsapp",
                            iconCategory = IconCategory.BRAND,
                            label = "WhatsApp"
                        )
                        FooterSocial(
                            href = "https://github.com/lennycodes2000",
                            iconName = "github",
                            iconCategory = IconCategory.BRAND,
                            label = "GitHub"
                        )
                    }
                }
            }

            // ---------------------------------------------------------
            // BOTTOM ROW — copyright + built-with
            // ---------------------------------------------------------
            Div(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("gap", "16px")
                        property("padding-top", "clamp(20px, 3vw, 28px)")
                        property("border-top", "1px solid rgba(255, 255, 255, 0.08)")
                    }.toAttrs()
            ) {

                P(
                    Modifier
                        .margin(0.px)
                        .fontSize(12.px)
                        .styleModifier {
                            property("color", GradeMateColors.TextOnDarkSecondary.toRgba(alpha = 0.65f))
                            property("letter-spacing", "0.02em")
                        }
                        .toAttrs()
                ) {
                    Text("© $year GradeMate. All rights reserved.")
                }

                // Back to top
                A(
                    href = "#home",
                    attrs = Modifier
                        .styleModifier {
                            property("display", "inline-flex")
                            property("align-items", "center")
                            property("gap", "6px")
                            property("text-decoration", "none")
                            property("font-size", "12px")
                            property("font-weight", "500")
                            property("color", GradeMateColors.TextOnDarkSecondary.toRgba(alpha = 0.65f))
                            property("transition", "color 200ms ease")
                        }
                        .toAttrs()
                ) {
                    Text("Back to top")
                    Span(
                        Modifier
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("width", "14px")
                                property("height", "14px")
                                property("font-size", "14px")
                                property("line-height", "1")
                            }.toAttrs()
                    ) {
                        Text("↑")
                    }
                }
            }
        }
    }
}